import { Component } from '@theme/component';
import { nudge, scrollToPick, sectionOf, syncAddToCart } from '@theme/craftly-pick-guard';

/**
 * Quantity picker — option cards with `− n +` steppers that must add up to a
 * fixed number of picks (palettes and varnish on kits for two, scents on
 * candle kits). Nothing is preselected unless the markup says so.
 *
 * The counts are the state; everything else is derived from them on each
 * change: the card's look, which buttons are enabled, the "x / N" pill, and
 * the numbered hidden inputs the product form submits (`Kvapas 1..N`). Those
 * are expanded in the order the options were first picked, so Vanilė ×2 then
 * Cinamonas ×1 reads `Kvapas 1: Vanilė`, `Kvapas 2: Vanilė`,
 * `Kvapas 3: Cinamonas` — the same order the customer built it in.
 *
 * Add to cart is held back while any picker in the section is incomplete —
 * see `craftly-pick-guard.js`.
 *
 * Every `+` dispatches a bubbling `craftly-qty:pick` event with the option's
 * value, so the palette gallery can show what was just picked.
 *
 * @extends {Component}
 */
export default class CraftlyQtyPicker extends Component {
  /**
   * Picks per option value, only for options with at least one pick. A Map
   * keeps insertion order, which is the pick order the inputs are expanded
   * in. Kept in JS rather than read back from the DOM, because a section
   * re-render morphs the DOM back to the server's defaults.
   * @type {Map<string, number>}
   */
  #counts = new Map();

  /**
   * The section this picker sat in when last rendered — kept so add to cart
   * can be re-checked after the picker is removed (e.g. switching to a
   * ceramics kit), when `closest()` no longer reaches it.
   * @type {ParentNode | null}
   */
  #scope = null;

  /**
   * Notices a section re-render. Horizon's morph keeps this element but never
   * calls `updatedCallback` on it: `morph.js` hands `onAfterUpdate` the freshly
   * parsed node rather than the live one, and the parsed copy is never an
   * upgraded Component. So watch for the markup changing under us instead.
   * Our own renders are discarded with `takeRecords()`, which leaves only
   * changes made from outside — i.e. the morph.
   */
  #observer = new MutationObserver(() => this.#reconcile());

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('click', this.#onClick);
    this.#counts = this.#readDefaults();
    this.#render();
    this.#observer.observe(this, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['data-max', 'data-count', 'data-default-count', 'data-value', 'value', 'disabled'],
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('click', this.#onClick);
    this.#observer.disconnect();

    const scope = this.#scope;
    if (scope) queueMicrotask(() => syncAddToCart(scope));
  }

  /**
   * Runs after a section re-render — a size or kit switch on the PDP, or an
   * in-place product swap on the homepage. The element survives the morph but
   * its markup now describes the new product, whose maximum may differ (2 → 4
   * scents going to XL). The picks carry over when they still fit, so going
   * up a size doesn't throw away what was chosen; otherwise the new product's
   * defaults apply.
   *
   * Driven by `#observer`; `updatedCallback` routes here too, in case a later
   * Horizon release starts calling it. Running twice is harmless.
   */
  #reconcile() {
    const values = new Set(this.#options.map((card) => card.dataset.value ?? ''));
    let total = 0;
    let fits = true;

    for (const [value, count] of this.#counts) {
      if (!values.has(value)) fits = false;
      total += count;
    }

    if (!fits || total > this.max) this.#counts = this.#readDefaults();

    this.#render();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#reconcile();
  }

  /** How many picks this kit takes. */
  get max() {
    return Number(this.dataset.max) || 0;
  }

  get total() {
    let total = 0;
    for (const count of this.#counts.values()) total += count;
    return total;
  }

  get isComplete() {
    return this.total >= this.max;
  }

  /** @returns {HTMLElement[]} */
  get #options() {
    return [...this.querySelectorAll('[data-qty-option]')].filter(
      /** @returns {el is HTMLElement} */ (el) => el instanceof HTMLElement
    );
  }

  /** @returns {Map<string, number>} */
  #readDefaults() {
    const counts = new Map();
    for (const card of this.#options) {
      const count = Number(card.dataset.defaultCount) || 0;
      if (count > 0) counts.set(card.dataset.value ?? '', count);
    }
    return counts;
  }

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    if (!(event.target instanceof Element)) return;

    const button = event.target.closest('[data-qty-step]');
    if (!(button instanceof HTMLButtonElement) || button.disabled || !this.contains(button)) return;

    const card = button.closest('[data-qty-option]');
    if (!(card instanceof HTMLElement)) return;

    const value = card.dataset.value ?? '';
    const step = Number(button.dataset.qtyStep);
    const current = this.#counts.get(value) ?? 0;
    const next = current + step;

    if (next < 0 || (step > 0 && this.isComplete)) return;

    // A first pick goes to the end of the order; a last one leaves it.
    if (next === 0) this.#counts.delete(value);
    else this.#counts.set(value, next);
    this.#render();

    if (step > 0) {
      this.dispatchEvent(new CustomEvent('craftly-qty:pick', { bubbles: true, detail: { value } }));
    }

    // The `−` disappears at zero; hand focus to the `+` on the same card so
    // keyboard users aren't dropped back to the top of the page.
    if (next === 0) {
      /** @type {HTMLButtonElement | null} */ (card.querySelector('[data-qty-step="1"]'))?.focus();
    }

    // The add-on picker rebuilds its `items[0]` payload from the form's
    // properties on `change`; setting `.value` from script fires nothing.
    this.dispatchEvent(new Event('change', { bubbles: true }));
  };

  /** Writes the counts into the cards, the pill, the inputs and add to cart. */
  #render() {
    const max = this.max;
    const total = this.total;
    const full = total >= max;

    for (const card of this.#options) {
      const count = this.#counts.get(card.dataset.value ?? '') ?? 0;
      card.dataset.count = String(count);

      const countEl = card.querySelector('[data-qty-count]');
      if (countEl) countEl.textContent = String(count);

      const minus = card.querySelector('[data-qty-step="-1"]');
      if (minus instanceof HTMLButtonElement) minus.disabled = count === 0;

      const plus = card.querySelector('[data-qty-step="1"]');
      if (plus instanceof HTMLButtonElement) plus.disabled = full;
    }

    const status = this.querySelector('[data-qty-status]');
    if (status) {
      // Only touch the text when it changes, so the live region doesn't
      // re-announce an identical value.
      const text = full ? this.dataset.completeLabel || '✓' : `${total} / ${max}`;
      if (status.textContent !== text) status.textContent = text;
      status.toggleAttribute('data-complete', full);
    }

    this.toggleAttribute('data-incomplete', !full);
    this.#syncInputs();

    if (full) this.#hideError();
    else if (!this.#error?.hidden) this.#showError();

    this.#scope = sectionOf(this);
    syncAddToCart(this.#scope);

    // Everything above was us, not a re-render.
    this.#observer.takeRecords();
  }

  /**
   * Expands the counts into `Prefix 1..N` in pick order. Unused slots are
   * disabled rather than left empty, so an empty `Kvapas n` is never sent,
   * and anything beyond the maximum (left over from a larger kit) is removed.
   */
  #syncInputs() {
    const picks = [];
    for (const [value, count] of this.#counts) {
      for (let i = 0; i < count; i++) picks.push(value);
    }

    const prefix = this.dataset.propertyPrefix || '';
    const inputs = /** @type {HTMLInputElement[]} */ ([...this.querySelectorAll('input[data-qty-input]')]);

    inputs.forEach((input, index) => {
      if (index >= this.max) {
        input.remove();
        return;
      }

      input.name = `properties[${prefix} ${index + 1}]`;
      const value = picks[index] ?? '';
      input.value = value;
      input.disabled = value === '';
    });
  }

  get #error() {
    return /** @type {HTMLElement | null} */ (this.querySelector('[data-qty-error]'));
  }

  #showError() {
    const error = this.#error;
    if (!error) return;

    const remaining = Math.max(this.max - this.total, 0);
    const template = this.dataset.remainingMessage || '{k}';
    error.textContent = template.replace('{k}', String(remaining));
    error.hidden = false;
  }

  #hideError() {
    const error = this.#error;
    if (error) error.hidden = true;
  }

  /**
   * Called when add to cart is pressed with this picker incomplete: brings it
   * into view, says what's missing and nudges the pill.
   */
  flag() {
    scrollToPick(this);
    this.#showError();

    const status = this.querySelector('[data-qty-status]');
    if (status) nudge(status);

    const firstPlus = this.querySelector('[data-qty-step="1"]:not(:disabled)');
    if (firstPlus instanceof HTMLElement) firstPlus.focus({ preventScroll: true });
  }
}

if (!customElements.get('craftly-qty-picker')) {
  customElements.define('craftly-qty-picker', CraftlyQtyPicker);
}
